import { useMutation } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { publicGraphqlClient } from '@/Public/StaffMessage/GraphqlClient';

const VALIDATE_SENDER = gql`
    mutation ValidateSender($input: createSenderStaffMessagingInput!) {
        createSenderStaffMessaging(input: $input) {
            senderStaffMessaging {
                id
                requiresVerification
            }
        }
    }
`;

// requestOtpStaffMessagingAck — re-issues a verification code for an existing sender.
// Input shape: SenderByEmailInput (same as validateSender).
const REQUEST_OTP = gql`
    mutation RequestOtp($input: requestOtpStaffMessagingAckInput!) {
        requestOtpStaffMessagingAck(input: $input) {
            staffMessagingAck {
                success
            }
        }
    }
`;

// sendMessageStaffMessagingAck — sends the message.
const SEND_MESSAGE = gql`
    mutation SendMessage($input: sendMessageStaffMessagingAckInput!) {
        sendMessageStaffMessagingAck(input: $input) {
            staffMessagingAck {
                success
            }
        }
    }
`;

export type ApiErrorCode = 'rate-limited' | 'captcha-failed' | 'otp-mismatch' | 'otp-expired' | 'sender-mismatch' | 'error';

export interface ValidateSenderResult {
    readonly senderIdHash: string;
    readonly requiresOtp: boolean;
}

export interface SenderData {
    readonly name: string;
    readonly email: string;
    readonly phone: string;
}

export interface MessageData {
    readonly subject: string;
    readonly body: string;
}

interface GraphqlErrorResponse {
    response?: { errors?: Array<{ extensions?: { code?: string } }> };
}

function extensionCode(err: unknown): string {
    return (err as GraphqlErrorResponse)?.response?.errors?.[0]?.extensions?.code ?? '';
}

function classifyCode(code: string): ApiErrorCode | null {
    if (code === 'RATE_LIMITED' || /rate.?limit/i.test(code)) return 'rate-limited';
    if (code === 'CAPTCHA_FAILED') return 'captcha-failed';
    if (code === 'OTP_EXPIRED') return 'otp-expired';
    if (code === 'OTP_ERROR') return 'otp-mismatch';
    if (code === 'SENDER_MISMATCH') return 'sender-mismatch';
    return null;
}

function classifyMessage(err: unknown): ApiErrorCode {
    const message = err instanceof Error ? err.message : String(err);
    if (/rate.?limit/i.test(message) || message.includes('RATE_LIMITED')) return 'rate-limited';
    if (message.includes('OTP_EXPIRED')) return 'otp-expired';
    if (/otp.?mismatch/i.test(message) || message.includes('OTP_MISMATCH')) return 'otp-mismatch';
    if (message.includes('SENDER_MISMATCH')) return 'sender-mismatch';
    return 'error';
}

function classifyError(err: unknown): ApiErrorCode {
    return classifyCode(extensionCode(err)) ?? classifyMessage(err);
}

interface ValidateSenderVars { email: string; recaptchaToken: string; bypassRecaptcha: boolean }
interface RequestOtpVars { email: string; recaptchaToken: string; bypassRecaptcha: boolean }
interface SendMessageVars { senderData: SenderData; messageData: MessageData; recipientId?: string; recipientAddress?: string; recaptchaToken: string; bypassRecaptcha: boolean; otpCode?: string }

export interface UseStaffMessageReturn {
    readonly validateSender: UseMutationResult<
        ValidateSenderResult | { readonly error: ApiErrorCode },
        Error,
        ValidateSenderVars
    >;
    readonly requestOtp: UseMutationResult<
        { readonly success: true } | { readonly error: ApiErrorCode },
        Error,
        RequestOtpVars
    >;
    readonly sendMessage: UseMutationResult<
        { readonly success: true } | { readonly error: ApiErrorCode },
        Error,
        SendMessageVars
    >;
}

export function useStaffMessage(): UseStaffMessageReturn {
    const validateSender = useMutation<
        ValidateSenderResult | { readonly error: ApiErrorCode },
        Error,
        ValidateSenderVars
    >({
        mutationFn: async ({ email, recaptchaToken, bypassRecaptcha }) => {
            try {
                const response = await publicGraphqlClient.request<{
                    createSenderStaffMessaging: {
                        senderStaffMessaging: { id: string; requiresVerification: boolean };
                    };
                }>(VALIDATE_SENDER, {
                    input: { email, recaptchaToken, bypassRecaptcha },
                });
                const { id, requiresVerification } =
                    response.createSenderStaffMessaging.senderStaffMessaging;
                return { senderIdHash: id, requiresOtp: requiresVerification };
            } catch (err) {
                return { error: classifyError(err) };
            }
        },
    });

    const requestOtp = useMutation<
        { readonly success: true } | { readonly error: ApiErrorCode },
        Error,
        RequestOtpVars
    >({
        mutationFn: async ({ email, recaptchaToken, bypassRecaptcha }) => {
            try {
                await publicGraphqlClient.request(REQUEST_OTP, {
                    input: { email, recaptchaToken, bypassRecaptcha },
                });
                return { success: true as const };
            } catch (err) {
                return { error: classifyError(err) };
            }
        },
    });

    const sendMessage = useMutation<
        { readonly success: true } | { readonly error: ApiErrorCode },
        Error,
        SendMessageVars
    >({
        mutationFn: async ({ senderData, messageData, recipientId, recipientAddress, recaptchaToken, bypassRecaptcha, otpCode }) => {
            try {
                await publicGraphqlClient.request(SEND_MESSAGE, {
                    input: {
                        name: senderData.name,
                        email: senderData.email,
                        phone: senderData.phone || null,
                        subject: messageData.subject,
                        message: messageData.body,
                        ...(recipientId ? { recipientId } : {}),
                        ...(recipientAddress ? { recipientAddress } : {}),
                        recaptchaToken,
                        bypassRecaptcha,
                        ...(otpCode !== undefined ? { otpCode } : {}),
                    },
                });
                return { success: true as const };
            } catch (err) {
                return { error: classifyError(err) };
            }
        },
    });

    return { validateSender, requestOtp, sendMessage };
}
